import type { ReactElement } from 'react';
import { ChannelType } from '@gr/core/distribution/channels/channel-types';
import type { ChannelDto } from '@gr/core/distribution/channels/dto';
import { useServices, useT } from '../../../app/di';
import { useAction, useResource } from '../../../shared/hooks';
import { Alert } from '../../../ui-kit/Alert/Alert';
import { Card } from '../../../ui-kit/Card/Card';
import { EmptyState } from '../../../ui-kit/EmptyState/EmptyState';
import { Loading } from '../../../ui-kit/Loading/Loading';
import { useBlog } from '../../accounts/workspaces';
import { ChannelCard } from './ChannelCard/ChannelCard';
import { ChannelForm } from './ChannelForm/ChannelForm';

// CHN-UC-1 … CHN-UC-5
export function ChannelsSection(): ReactElement {
  const { api } = useServices();
  const { t, te } = useT();
  const { blog } = useBlog();
  const channels = useResource(() => api.channels.list(blog.id), [api, blog.id]);

  return (
    <div className="u-stack-lg">
      <p className="u-muted">{t('channels.subtitle')}</p>
      {channels.loading && !channels.data ? (
        <Loading label={t('common.loading')} />
      ) : channels.error ? (
        <Alert tone="danger">{te(channels.error)}</Alert>
      ) : channels.data && channels.data.length > 0 ? (
        channels.data.map((channel) => <ConnectedChannelCard key={channel.id} channel={channel} onChanged={() => void channels.reload()} />)
      ) : (
        <EmptyState title={t('channels.empty')} text={t('channels.emptyText')} />
      )}
      <Card title={t('channels.add')}>
        <ChannelForm
          initial={{ type: ChannelType.Telegram, name: '', url: '' }}
          withType
          clearOnSuccess
          submitLabel={t('common.add')}
          onSubmit={async (values) => {
            const result = await api.channels.create(blog.id, values);
            if (result.success) await channels.reload();
            return result;
          }}
        />
      </Card>
    </div>
  );
}

/** Подключает карточку канала к API: правка, слоты, включение, удаление. */
function ConnectedChannelCard({ channel, onChanged }: { channel: ChannelDto; onChanged(): void }): ReactElement {
  const { api } = useServices();
  const { blog } = useBlog();
  const update = useAction((patch: { name?: string; url?: string; active?: boolean }) => api.channels.update(blog.id, channel.id, patch));
  const slots = useAction((list: ChannelDto['slots']) => api.channels.setSlots(blog.id, channel.id, list));
  const remove = useAction(() => api.channels.delete(blog.id, channel.id));

  return (
    <ChannelCard
      channel={channel}
      timezone={blog.timezone}
      pending={update.pending || remove.pending}
      savingSlots={slots.pending}
      error={update.error ?? slots.error ?? remove.error}
      onToggleActive={async () => {
        if ((await update.run({ active: !channel.active })).success) onChanged();
      }}
      onDelete={async () => {
        if ((await remove.run()).success) onChanged();
      }}
      onSaveDetails={async ({ name, url }) => {
        const result = await api.channels.update(blog.id, channel.id, { name, url });
        if (result.success) onChanged();
        return result;
      }}
      onSaveSlots={async (list) => {
        const result = await slots.run(list);
        if (result.success) onChanged();
        return result;
      }}
    />
  );
}
